import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, forkJoin, of } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { Table } from '../../shared/table/table';
import { Popup } from '../../../core/shared/components/popup/popup';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';
import { ToastService } from '../../../core/shared/services/toast.service';
import { AppIcons } from '../../../core/icons/fa-icons';
import { CraditService } from '../shared/services/cradit.service';
import {
  ProposedDisbursementListRowDto,
  ProposedDisbursementRow,
} from '../shared/models/proposed-disbursement.model';

@Component({
  selector: 'app-proposed-disbursement-list',
  imports: [CommonModule, Table, Popup, TranslatePipe],
  templateUrl: './proposed-disbursement-list.html',
  styleUrl: './proposed-disbursement-list.css',
})
export class ProposedDisbursementList implements OnInit {
  constructor(
    private creditService: CraditService,
    private pickListService: PickListService,
    private toast: ToastService,
    private router: Router,
  ) {}

  // ================= PROPOSED DISBURSEMENT LIST =================
  disbursementList: ProposedDisbursementRow[] = [];
  disbursementColumns: TableColumn[] = [];
  disbursementLoading = false;
  disbursementError = '';

  // ================= VIEW POPUP =================
  showViewPopup = false;
  selectedRow: ProposedDisbursementRow | null = null;

  /**
   * Disbursement status labels (`stsKey`) come from ref code group 33; a code
   * not found there is looked up in group 26. Groups are scanned in order.
   */
  private readonly statusGroups = [PKL_GRP.DISBURSEMENT_STATUS, PKL_GRP.PANDDING_STATUS];

  ngOnInit(): void {
    this.initDisbursementColumns();

    // Status labels come from the picklist store; reload the rows once this
    // batch is actually in the store so the labels resolve.
    this.pickListService.loadCodesAsync(this.statusGroups).subscribe({
      next: () => this.loadProposedDisbursements(),
      error: (error: any) => {
        console.error('Unable to load status picklist:', error);

        this.loadProposedDisbursements();
      },
    });
  }

  // ================= LOAD =================
  loadProposedDisbursements(): void {
    this.disbursementLoading = true;
    this.disbursementError = '';

    // Pending list first, then the full list; either can fail on its own.
    forkJoin({
      pending: this.creditService.getPendingProposedDisbursementList().pipe(
        catchError((error) => {
          console.error('Unable to load pending proposed disbursements:', error);
          return of([]);
        }),
      ),
      all: this.creditService.getProposedDisbursementList(),
    }).subscribe({
      next: ({ pending, all }) => {
        const pendingRows = this.toDisbursementRows(pending);
        const pendingSeqs = new Set(pendingRows.map((row) => row.prpsdDsbmntSeq));

        // Pending rows on top; the old list below, without the rows already shown above.
        this.disbursementList = [
          ...pendingRows,
          ...this.toDisbursementRows(all).filter((row) => !pendingSeqs.has(row.prpsdDsbmntSeq)),
        ];

        this.disbursementLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load proposed disbursements:', error);

        this.disbursementList = [];
        this.disbursementError = 'CREDIT_PROPOSED_DISB.LOAD_FAILED';
        this.disbursementLoading = false;
      },
    });
  }

  /** Rows of a list response (bare array, `{ data }`, or a single object), in the API's own order. */
  private toDisbursementRows(response: any): ProposedDisbursementRow[] {
    // The endpoint returns a single object when only one row exists
    const rows: ProposedDisbursementListRowDto[] = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : response && typeof response === 'object' && !response.__hasError && !response.error
          ? [response]
          : [];

    return rows
      .filter((item) => Number(item.delFlg ?? 0) !== 1)
      .map((item) => this.mapDisbursementToRow(item));
  }

  private mapDisbursementToRow(item: ProposedDisbursementListRowDto): ProposedDisbursementRow {
    const status = item.stsKey != null ? Number(item.stsKey) : null;

    return {
      prpsdDsbmntSeq: item.prpsdDsbmntSeq != null ? Number(item.prpsdDsbmntSeq) : null,
      crdtPrpslSeq: item.crdtPrpslSeq != null ? Number(item.crdtPrpslSeq) : null,
      clntSeq: item.clntSeq != null ? Number(item.clntSeq) : null,
      institutionNm: (item.institutionNm || '').trim() || '-',
      installmentNo: item.installmentNo != null ? Number(item.installmentNo) : null,
      schMth: item.schMth ?? null,
      prpsdDsbmntAmnt: Number(item.prpsdDsbmntAmnt ?? 0),
      status: status,
      statusLabel: this.resolveStatusLabel(status),
    };
  }

  /** Falls back to the raw code when the status is in none of the status groups. */
  private resolveStatusLabel(status: number | null): string {
    if (status == null) {
      return '-';
    }

    for (const group of this.statusGroups) {
      const option = this.pickListService
        .getOptionsSync(group)
        .find((item) => Number(item.value) === status);

      if (option) {
        return option.label;
      }
    }

    return String(status);
  }

  // ================= TABLE CONFIG =================
  private initDisbursementColumns(): void {
    this.disbursementColumns = [
      {
        key: 'prpsdDsbmntSeq',
        label: 'CREDIT_PROPOSED_DISB.DISBURSEMENT_ID',
        type: 'text',
      },
      {
        key: 'crdtPrpslSeq',
        label: 'CREDIT_UPLOAD.CREDIT_PROPOSAL_ID',
        type: 'text',
      },
      {
        key: 'institutionNm',
        label: 'QPR.INSTITUTION',
        type: 'text',
      },
      {
        key: 'installmentNo',
        label: 'CREDIT_PROPOSED_DISB.INSTALLMENT_NO',
        type: 'text',
      },
      {
        key: 'schMth',
        label: 'CREDIT_PROPOSED_DISB.SCHEDULED_MONTH',
        type: 'date',
      },
      {
        key: 'prpsdDsbmntAmnt',
        label: 'CREDIT_PROPOSED_DISB.PROPOSED_AMOUNT',
        type: 'currency',
      },
      {
        key: 'statusLabel',
        label: 'COMMON.STATUS',
        type: 'text',
      },
    ];
  }

  disbursementActions: TableAction[] = [
    {
      icon: AppIcons.view,
      tooltip: 'COMMON.VIEW',
      class: 'text-blue-600',
      onClick: (row: ProposedDisbursementRow) => this.handleView(row),
    },
    {
      icon: AppIcons.rupee,
      tooltip: 'CREDIT_PROPOSED_DISB.DISBURSEMENT_NOTE',
      class: 'text-indigo-600',
      onClick: (row: ProposedDisbursementRow) => this.openDisbursementNote(row),
      visible: (row: ProposedDisbursementRow) => Number(row?.prpsdDsbmntSeq) > 0,
    },
  ];

  // ================= ACTIONS =================
  handleView(row: ProposedDisbursementRow): void {
    this.selectedRow = row;
    this.showViewPopup = true;
  }

  closeViewPopup(): void {
    this.showViewPopup = false;
    this.selectedRow = null;
  }

  openDisbursementNote(row: ProposedDisbursementRow): void {
    if (!row?.prpsdDsbmntSeq || row.prpsdDsbmntSeq <= 0) {
      this.toast.error('CREDIT_PROPOSED_DISB.SEQ_NA');
      return;
    }

    this.router.navigate(['/disbursement-note', row.prpsdDsbmntSeq]);
  }
}
